import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { CrewMemberStatusBadge } from "@/components/crew/crew-member-status-badge"
import HeadingSmall from "@/components/heading-small"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { currentBrandPath } from "@/routes"
import type { CrewMembershipStatus } from "@/types/crew"

/**
 * OKS-40 — Settings → Memberships page (creator-only).
 *
 * Read-only roll-up of every brand the creator has joined. Each
 * row exposes brand name + logo + joined date + status badge.
 * When the membership is switchable (active or paused on a brand
 * that's NOT currently active), a "Switch to" Form posts to
 * `currentBrandPath()` (OKS-24 brand-switcher reuse) which
 * triggers a full reload into the new brand context.
 *
 * Empty state: zero memberships → friendly message (PRD §1
 * post-archive scenario).
 */

interface Membership {
  id: number
  brand_id: number
  brand_name: string
  brand_slug: string
  brand_logo_url: string | null
  status:
    | "pending_charter"
    | "active"
    | "paused"
    | "archived"
    | "mismatch_quarantine"
  joined_at: string | null
  switchable: boolean
}

export interface SettingsMembershipsProps {
  memberships: Membership[]
  active_brand_id: number | null
}

export default function SettingsMemberships({
  memberships,
  active_brand_id,
}: SettingsMembershipsProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("settings.creator.memberships.title")} />

      <SettingsLayout>
        <div data-testid="settings-memberships" className="space-y-6">
          <HeadingSmall
            title={t("settings.creator.memberships.title")}
            description={t("settings.creator.memberships.description")}
          />

          {memberships.length === 0 ? (
            <Card data-testid="settings-memberships-empty">
              <CardContent className="text-muted-foreground py-12 text-center text-sm">
                {t("settings.creator.memberships.empty")}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mobile: stacked cards */}
              <div className="space-y-3 md:hidden">
                {memberships.map((m) => (
                  <Card
                    key={m.id}
                    data-testid={`settings-memberships-card-${m.id}`}
                  >
                    <CardContent className="flex items-start justify-between gap-3 py-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10 rounded-md">
                          {m.brand_logo_url ? (
                            <AvatarImage src={m.brand_logo_url} alt="" />
                          ) : null}
                          <AvatarFallback className="rounded-md">
                            {m.brand_name.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{m.brand_name}</p>
                          {m.joined_at ? (
                            <p className="text-muted-foreground text-xs">
                              {t("settings.creator.memberships.joined", {
                                date: new Date(
                                  m.joined_at,
                                ).toLocaleDateString(),
                              })}
                            </p>
                          ) : null}
                          <MembershipStatusBadge
                            status={m.status}
                            className="mt-1"
                          />
                          {m.brand_id === active_brand_id ? (
                            <span className="text-muted-foreground ml-2 text-xs">
                              · {t("settings.creator.memberships.active_now")}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {m.switchable ? (
                        <SwitchButton brandId={m.brand_id} />
                      ) : null}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop: table */}
              <div className="hidden md:block">
                <Table data-testid="settings-memberships-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t("settings.creator.memberships.col_brand")}
                      </TableHead>
                      <TableHead>
                        {t("settings.creator.memberships.col_joined")}
                      </TableHead>
                      <TableHead>
                        {t("settings.creator.memberships.col_status")}
                      </TableHead>
                      <TableHead className="text-right">
                        <span className="sr-only">
                          {t("settings.creator.memberships.col_actions")}
                        </span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberships.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8 rounded-md">
                              {m.brand_logo_url ? (
                                <AvatarImage src={m.brand_logo_url} alt="" />
                              ) : null}
                              <AvatarFallback className="rounded-md text-xs">
                                {m.brand_name.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <span className="font-medium">
                                {m.brand_name}
                              </span>
                              {m.brand_id === active_brand_id ? (
                                <span className="text-muted-foreground ml-2 text-xs">
                                  ·{" "}
                                  {t("settings.creator.memberships.active_now")}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {m.joined_at
                            ? new Date(m.joined_at).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <MembershipStatusBadge status={m.status} />
                        </TableCell>
                        <TableCell className="text-right">
                          {m.switchable ? (
                            <SwitchButton brandId={m.brand_id} />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}

/**
 * Wraps `CrewMemberStatusBadge` with a fallback for the rare
 * `mismatch_quarantine` state (creator's TikTok handle was
 * reassigned — landed in OKS-23). The shared CrewMemberStatusBadge
 * is a 4-state component owned by OKS-25/26; the 5th DB-allowed
 * state surfaces here only on the creator's own memberships
 * read-out, so we render a generic destructive Badge inline.
 */
function MembershipStatusBadge({
  status,
  className,
}: {
  status: Membership["status"]
  className?: string
}) {
  const { t } = useTranslation()
  if (status === "mismatch_quarantine") {
    return (
      <Badge variant="destructive" className={className}>
        {t("settings.creator.memberships.status.mismatch_quarantine")}
      </Badge>
    )
  }
  return (
    <CrewMemberStatusBadge
      status={status as CrewMembershipStatus}
      className={className}
    />
  )
}

function SwitchButton({ brandId }: { brandId: number }) {
  const { t } = useTranslation()
  return (
    <Form
      method="post"
      action={currentBrandPath()}
      options={{ preserveScroll: false }}
      className="inline-block"
    >
      <input type="hidden" name="brand_id" value={brandId} />
      <Button
        type="submit"
        size="sm"
        variant="outline"
        data-testid={`settings-memberships-switch-${brandId}`}
      >
        {t("settings.creator.memberships.switch_to")}
      </Button>
    </Form>
  )
}
